import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../i18n/I18nContext";
import { api } from "../../lib/api";
import { CalendarDays, Wallet, TrendingUp, Users, AlertTriangle, Package, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";

const COLORS = ["#1E40AF", "#EA580C", "#D97706", "#DC2626", "#0891b2", "#16a34a"];

export default function Overview() {
  const { t, fmtSAR, fmtDate, isAr, localize } = useI18n();
  const [d, setD] = useState(null);
  const [rep, setRep] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setD(r.data)).catch(() => {});
    api.get("/admin/reports/summary").then((r) => setRep(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const bn = (id) => { const b = branches.find((x) => x.id === id); return b ? (isAr ? b.name_ar : b.name_en) : "—"; };
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Riyadh" });
  const todays = bookings.filter((b) => (b.start || "").slice(0, 10) === today && b.status !== "cancelled");
  const upcoming = bookings.filter((b) => new Date(b.start) > new Date() && b.status !== "cancelled").slice(0, 6);

  const statusData = rep ? Object.entries(rep.status_counts || {}).map(([k, v]) => ({ name: k, value: v })) : [];
  const branchData = rep ? Object.entries(rep.by_branch || {}).map(([k, v]) => ({ name: bn(k), value: Math.round(v.value) })) : [];

  const cards = d ? [
    [CalendarDays, t("admin.kpi_bookings"), d.bookings_count, "text-primary bg-primary/10"],
    [Clock, isAr ? "حجوزات اليوم" : "Today", todays.length, "text-cyan-600 bg-cyan-100"],
    [Wallet, t("admin.kpi_revenue"), fmtSAR(d.collected_revenue), "text-green-600 bg-green-100"],
    [TrendingUp, t("admin.kpi_value"), fmtSAR(d.booking_value), "text-[hsl(var(--brand-orange))] bg-orange-100"],
    [Users, t("admin.kpi_customers"), d.customers, "text-[hsl(var(--brand-yellow))] bg-amber-100"],
    [Package, isAr ? "طلبات معلّقة" : "Pending orders", d.pending_orders, "text-indigo-600 bg-indigo-100"],
    [AlertTriangle, t("admin.kpi_complaints"), d.open_complaints, "text-[hsl(var(--brand-red))] bg-red-100"],
  ] : [];

  return (
    <div>
      <h1 className="text-xl font-bold text-slate-900">{t("admin.overview")}</h1>
      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        {cards.map(([Icon, label, val, cls], i) => (
          <div key={i} data-testid={`kpi-${i}`} className="rounded-xl border border-border bg-white p-4">
            <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${cls}`}><Icon className="h-4 w-4" /></div>
            <div className="mt-3 text-xl font-extrabold text-slate-900">{val}</div>
            <div className="text-xs text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-white p-5">
          <h3 className="mb-4 font-semibold text-slate-900">{isAr ? "الحجوزات حسب الحالة" : "Bookings by status"}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={statusData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>{statusData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-border bg-white p-5">
          <h3 className="mb-4 font-semibold text-slate-900">{isAr ? "قيمة الحجوزات حسب الفرع" : "Booking value by branch"}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={branchData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={(e) => e.name}>
                {branchData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtSAR(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">{isAr ? "الحجوزات القادمة" : "Upcoming bookings"}</h3>
          <Link to="/admin/bookings" className="text-sm font-medium text-primary hover:underline">{t("common.all")} →</Link>
        </div>
        <div className="divide-y divide-border">
          {upcoming.map((b) => (
            <div key={b.id} className="flex items-center justify-between py-2.5 text-sm">
              <div><span className="font-medium text-slate-900">{b.customer_name}</span><span className="ms-2 text-slate-400">{bn(b.branch_id)}</span></div>
              <div className="text-slate-500">{fmtDate(b.start).greg} · {fmtDate(b.start).time}</div>
              <div className="font-semibold text-primary">{fmtSAR(b.amount_total)}</div>
            </div>
          ))}
          {upcoming.length === 0 && <div className="py-6 text-center text-slate-400">{t("common.none")}</div>}
        </div>
      </div>
      {d && <p className="mt-4 rounded-lg bg-slate-100 p-3 text-xs text-slate-500">ℹ {isAr ? d.note_ar : d.note_en}</p>}
    </div>
  );
}
